import { grossMargin, suggestedPrice } from '@kia/shared';

/**
 * Pricing guidance for ONE line/product (E16 — follow the owner to every price). Pure, so the numbers the
 * hint shows are unit-tested to the corner. `cogs` is the per-unit بهای تمام‌شده, `price` the net sale price,
 * `floor` the margin floor (0..1).
 */
export interface PricingView {
  /** (price − cogs) / price, or null when there is no positive price to take a margin on. */
  margin: number | null;
  /** the price that hits the TARGET margin exactly: cogs ÷ (1 − target); 0 when cost is unknown or target ≥ 1.
   *  With target defaulting to floor, this is the floor price until the owner raises his aim. */
  suggested: number;
  /** true when a real margin sits below the FLOOR (a loss or thin deal — the hard guardrail). */
  belowFloor: boolean;
  /** true when a real margin sits below the TARGET but at/above the floor (acceptable, but off the aim). */
  belowTarget: boolean;
}

/**
 * Pricing guidance for one line. The suggested price steers to the owner's `target` margin (not the floor),
 * so the app aims at the goal; `floor` remains the guardrail the margin must clear. `target` defaults to
 * `floor` ⇒ callers that pass only (cogs, price, floor) get exactly today's floor-based suggestion.
 */
export function pricingFor(
  cogs: number,
  price: number,
  floor: number,
  target: number = floor,
): PricingView {
  // margin + suggested come from the ONE shared authority (@kia/shared) so the hint, the AI brief and the
  // sense-check can never show a different margin or a different suggested price for the same motor (CW-07).
  const margin = grossMargin(price, cogs);
  const suggested = suggestedPrice(cogs, target);
  const belowFloor = margin != null && margin < floor;
  const belowTarget = margin != null && margin < target;
  return { margin, suggested, belowFloor, belowTarget };
}
